import React, { memo } from 'react'
import classNames from 'classnames'
import { Bullet } from 'components/atoms/icons'
import { FormattedDateTimeRange } from 'transfix-ui/components/FormattedDateTimeRange'
import { MapMarker } from 'transfix-ui/components/MapMarker'
import { Tag, TAG_SIZE, TAG_STYLE } from 'transfix-ui/components/Tag'
import { Box, makeStyles } from 'transfix-ui/core'
import { IFullStop } from 'types'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { useStopConfirmationItem } from 'apps/fleet-planner/components/load-details/stops//StopsSummaryStyles'
import { capitalize } from 'utils/capitalize'
import { formatCityState } from 'utils/formatCityState'
import { AllowedTimezones } from 'constants/AllowedTimezones'
import { DISPLAY } from 'constants/display'
import { ReadMore } from './ReadMore'

interface IMarketplaceStopSummaryItem {
  stop?: IFullStop
  stopNumber: number
  className?: string
  compact?: boolean
  last?: boolean
}

const useStyles = makeStyles(theme => ({
  dateTime: {
    marginTop: theme.spacing(1),
  },
}))

export const MarketplaceStopConfirmationItem = memo<IMarketplaceStopSummaryItem>(
  ({ className, compact = false, last, stop, stopNumber }) => {
    const rootClasses = useStyles()
    const styles = useStopConfirmationItem({ compact, last })
    const { getFlag } = useFeatureFlagContext()
    const { enabled: fmsLoadDetailsInformation } = getFlag('fmsLoadDetailsInformation')
    const { appointment, facility, type: stopType } = stop || {}
    const { city: locality, stateCode: subdivisionCode } = facility || {}
    const { endAt = '', startAt = '', timezone: displayTimezone } = appointment || {}

    const timezone = displayTimezone as AllowedTimezones
    const classes = classNames(styles.root, className)

    const { info, isDropTrailer } = stop || {}
    const { commodity, genericCommodity, palletCount, specialInstruction, weight } = info || {}
    const computedCommodity = genericCommodity || commodity

    return (
      <div className={classes}>
        <div className={styles.symbol}>
          <MapMarker type={stopType} sequence={stopNumber} />
        </div>
        <div>
          <Box display='flex'>
            <Box className='body-large' mr={2}>
              {formatCityState(locality, subdivisionCode)}{' '}
            </Box>
            {isDropTrailer && (
              <Tag
                label='Drop Trailer'
                size={TAG_SIZE.xsmall}
                tagStyle={TAG_STYLE.info}
                outlineOnly
              />
            )}
          </Box>
          <FormattedDateTimeRange
            className={rootClasses.dateTime}
            startAt={startAt}
            endAt={endAt}
            startAtTimezone={timezone}
            endAtTimezone={timezone}
            format='time'
            as='div'
            timeShort
          />
          <Box mt={1}>
            <Box component='span'>
              {computedCommodity ? capitalize(computedCommodity) : 'No commodity'}
            </Box>
            <Bullet />
            <Box component='span'>{weight ? weight.toLocaleString() : DISPLAY.empty} lbs</Box>
            <Bullet />
            <Box component='span'>{`${palletCount || DISPLAY.empty} pallets`}</Box>
          </Box>
          {fmsLoadDetailsInformation && (
            <Box mt={1}>
              {specialInstruction ? (
                <ReadMore text={specialInstruction} maxCharacters={72} />
              ) : (
                DISPLAY.empty
              )}
            </Box>
          )}
        </div>
      </div>
    )
  }
)
